/// <reference types="signalr" />

import { Subject } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { environment } from '../environments/environment';
import { SessionService } from './session.service';
import { MatchSyncService } from './match-sync.service';
import { GoalUpdate, Match } from './models/match.model';

@Injectable({
  providedIn: 'root'
})
export class LiveDataService {
  private readonly connection: SignalR.Hub.Connection;
  private readonly proxy: SignalR.Hub.Proxy;
  private handlersRegistered = false;
  private hasStarted = false;
  private hasConnected = false;
  private shouldReconnect = false;
  readonly connectionRestored = new Subject<void>();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly sessionService = inject(SessionService);
  private readonly matchSyncService = inject(MatchSyncService);

  constructor() {
    type HubFactory = (url: string) => SignalR.Hub.Connection;
    const hubFactory = (globalThis as unknown as { $: { hubConnection: HubFactory } }).$.hubConnection;
    this.connection = hubFactory(`${environment.apiUrl}/signalr`);
    this.proxy = this.connection.createHubProxy('bettingticker');
  }

  connect(): void {
    this.shouldReconnect = true;
    if (!this.handlersRegistered) {
      this.registerHandlers(this.proxy);
      this.registerConnectionLifecycle(this.connection as SignalR.Hub.Connection & Record<string, unknown>);
      this.handlersRegistered = true;
    }

    if (this.hasStarted) {
      return;
    }

    this.clearReconnectTimer();
    this.hasStarted = true;
    this.startConnection(this.connection);
  }

  disconnect(): void {
    this.shouldReconnect = false;
    this.hasConnected = false;
    this.clearReconnectTimer();
    this.hasStarted = false;

    const connection = this.connection as SignalR.Hub.Connection & Partial<{ stop: () => void }>;
    connection.stop?.();
  }

  private registerHandlers(proxy: SignalR.Hub.Proxy): void {
    proxy.on('OnLiveGameInfo', payload => {
      console.log('OnLiveGameInfo', payload);
      const items = Array.isArray(payload) ? payload : [payload];
      for (const item of items) {
        if (this.isGoalUpdate(item)) {
          this.matchSyncService.applyGoalUpdate(item);
        }
      }
    });

    proxy.on('updateMatches', payload => {
      console.log('updateMatches', payload);
      const matches = this.toMatchArray(payload);
      this.matchSyncService.applyMatches(matches);
    });

    proxy.on('addOrUpdateMatch', payload => {
      console.log('addOrUpdateMatch', payload);
      const matches = this.toMatchArray(payload);
      this.matchSyncService.applyMatches(matches);
    });

    proxy.on('addMatch', payload => {
      console.log('addMatch', payload);
      const matches = this.toMatchArray(payload);
      this.matchSyncService.applyMatches(matches);
    });

    proxy.on('removeBetTypes', payload => {
      console.log('removeBetTypes', payload);
      this.matchSyncService.removeBetTypes(this.toBetTypeIds(payload));
    });

    proxy.on('removeMatch', payload => {
      console.log('removeMatch', payload);
      this.sessionService.removeMatch(payload as number);
    });
  }

  private toMatchArray(payload: unknown): Match[] {
    return (Array.isArray(payload) ? payload : [payload]) as Match[];
  }

  private toBetTypeIds(payload: unknown): number[] {
    return (payload as { Id: number }[]).map(item => item.Id);
  }

  private isGoalUpdate(payload: unknown): payload is GoalUpdate {
    return typeof payload === 'object'
      && payload !== null
      && (
        ('MatchID' in payload && typeof payload.MatchID === 'number')
        || ('Id' in payload && typeof payload.Id === 'number')
      )
      && (
        ('NewScore' in payload && typeof payload.NewScore === 'string')
        || ('Score' in payload && typeof payload.Score === 'string')
      );
  }

  private registerConnectionLifecycle(connection: SignalR.Hub.Connection & Record<string, unknown>): void {
    this.registerConnectionCallback(connection, 'connectionSlow', () => {
      console.log('SignalR connection slow');
    });

    this.registerConnectionCallback(connection, 'reconnecting', () => {
      console.log('SignalR reconnecting...');
    });

    this.registerConnectionCallback(connection, 'reconnected', () => {
      console.log('SignalR reconnected!');
      this.clearReconnectTimer();
      this.onConnected();
    });

    this.registerConnectionCallback(connection, 'disconnected', () => {
      console.log('SignalR disconnected. Retrying in 5 seconds.');
      this.hasStarted = false;
      this.scheduleReconnect();
    });

    this.registerConnectionCallback(connection, 'stateChanged', () => {
      console.log('SignalR state changed');
    });

    this.registerConnectionCallback(connection, 'error', () => {
      console.error('SignalR hub error');
    });
  }

  private registerConnectionCallback(
    connection: SignalR.Hub.Connection & Record<string, unknown>,
    eventName: string,
    callback: () => void
  ): void {
    const handler = connection[eventName];
    if (typeof handler === 'function') {
      handler.call(connection, callback);
    }
  }

  private startConnection(connection: SignalR.Hub.Connection): void {
    connection
      .start()
      .done(() => {
        this.clearReconnectTimer();
        console.log('SignalR connected!');
        this.onConnected();
      })
      .fail((err: unknown) => {
        this.hasStarted = false;
        console.error('SignalR connection error:', err);
        this.scheduleReconnect();
      });
  }

  private onConnected(): void {
    if (!this.shouldReconnect) {
      return;
    }
    const restored = this.hasConnected;
    this.hasConnected = true;
    if (restored) {
      this.connectionRestored.next();
    }
  }

  private scheduleReconnect(): void {
    if (!this.shouldReconnect || this.reconnectTimer !== null) {
      return;
    }

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, 5000);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer === null) {
      return;
    }

    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }
}
